<template>
  <AModal
    :model-value="modelValue"
    size="xl"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #title>
      <span class="lvv__title">
        <i class="material-icons-round lvv__title-icon">description</i>
        v{{ version }} 본문
      </span>
    </template>
    <template #sub>{{ documentId }}</template>

    <div v-if="loading" class="lvv__loading">
      <div class="p-spinner p-spinner--lg"></div>
      <p>본문을 불러오는 중...</p>
    </div>

    <div v-else-if="!data" class="lvv__loading">
      <p>본문을 불러오지 못했습니다.</p>
    </div>

    <div v-else class="lvv">
      <!-- 증빙 조회에서 열었을 때 "사용자가 무엇에 동의했는지"를 바로 알 수 있도록 메타를 함께 보여줍니다 -->
      <div class="lvv__meta">
        <APill :kind="statusPill.kind">{{ statusPill.label }}</APill>
        <!-- 이 모달은 문서 마스터를 모르므로 재동의 여부를 단정하지 않고 플래그만 표시합니다. -->
        <APill v-if="data.requiresReconsent" kind="err">실질 개정</APill>
        <APill v-else kind="default">경미 개정</APill>
        <span class="lvv__meta-item"><b>시행</b> {{ fmtKst(data.effectiveAt) }}</span>
        <span class="lvv__meta-item"><b>발행</b> {{ fmtKst(data.publishedAt) }}</span>
        <span class="lvv__meta-item"><b>작성자</b> {{ data.createdBy || '-' }}</span>
      </div>

      <div v-if="availableLangs.length" class="p-tabs lvv__tabs">
        <button
          v-for="lang in availableLangs"
          :key="lang"
          type="button"
          :class="['p-tabs__tab', { 'is-active': activeLang === lang }]"
          @click="activeLang = lang"
        >
          {{ langLabel(lang) }}
        </button>
      </div>

      <div v-if="current" class="lvv__doc">
        <h3 class="lvv__doc-title">{{ current.title || '(제목 없음)' }}</h3>
        <!-- 관리자가 에디터로 작성한 HTML 이라 v-html 이 불가피합니다. -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="lvv__doc-body" v-html="current.content"></div>
      </div>
      <div v-else class="lvv__empty">등록된 본문이 없습니다.</div>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">닫기</ABtn>
    </template>
  </AModal>
</template>

<script>
import swal from "sweetalert2";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import { apiErrorMessage } from "@/utils/apiError";
import { fmtKst, langLabel, isFuture, VERSION_STATUS } from "@/utils/legal";

export default {
  name: "LegalVersionViewerModal",
  components: { AModal, ABtn, APill },
  props: {
    modelValue: { type: Boolean, default: false },
    documentId: { type: String, default: "" },
    version: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      loading: false,
      data: null,
      activeLang: "ko",
    };
  },
  computed: {
    availableLangs() {
      return Object.keys(this.data?.contents || {});
    },
    current() {
      return this.data?.contents?.[this.activeLang] || null;
    },
    statusPill() {
      const meta = VERSION_STATUS[this.data?.status] || { label: this.data?.status, pill: "default" };
      if (this.data?.status === "published" && isFuture(this.data.effectiveAt)) {
        return { kind: "info", label: `시행 예정 ${fmtKst(this.data.effectiveAt, { withTime: false })}` };
      }
      return { kind: meta.pill, label: meta.label };
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.load();
    },
  },
  methods: {
    fmtKst,
    langLabel,
    async load() {
      if (!this.documentId || !this.version) return;
      this.loading = true;
      this.data = null;
      try {
        // 공개 API 는 발행본만 주므로 초안 미리보기까지 되는 관리자 상세를 씁니다.
        const res = await this.$axios({
          method: "get",
          url: `/emp/legal/documents/${encodeURIComponent(this.documentId)}/versions/${encodeURIComponent(this.version)}`,
          loadingBar: true,
        });
        this.data = res.data || null;
        const langs = Object.keys(this.data?.contents || {});
        this.activeLang = langs.includes("ko") ? "ko" : langs[0] || "ko";
      } catch (e) {
        console.error("본문 조회 실패:", e);
        swal.fire("오류", apiErrorMessage(e, "본문을 불러오지 못했습니다."), "error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.lvv {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 20px !important;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 56px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 11px 13px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
  }

  &__meta-item {
    font-size: 11px;
    color: $p-ink-mid;

    b {
      color: $p-ink-soft;
      font-weight: 800;
      margin-right: 4px;
    }
  }

  &__doc {
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 22px 24px;
    background: $p-panel;
    max-height: 55vh;
    overflow-y: auto;
  }

  &__doc-title {
    margin: 0 0 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid $p-line-soft;
    font-size: 16px;
    font-weight: 800;
    color: $p-ink;
  }

  &__doc-body {
    font-size: 13px;
    line-height: 1.75;
    color: $p-ink;

    :deep(h1), :deep(h2), :deep(h3), :deep(h4) {
      margin: 18px 0 8px;
      font-weight: 800;
      color: $p-ink;
    }
    :deep(h3) { font-size: 14px; }
    :deep(p) { margin: 0 0 10px; }
    :deep(ul), :deep(ol) { margin: 0 0 10px; padding-left: 20px; }
    :deep(table) {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0;
    }
    :deep(td), :deep(th) {
      border: 1px solid $p-line;
      padding: 7px 9px;
      font-size: 12px;
    }
  }

  &__empty {
    padding: 40px;
    text-align: center;
    font-size: 12px;
    color: $p-ink-soft;
  }
}
</style>
